// Captura + tokenización de tarjeta con el CardPayment Brick de MercadoPago.
//
// Componente AISLADO: solo monta el Brick y mapea sus callbacks. La
// orquestación (loading / éxito / error / reintento) la maneja el padre
// (Checkout) vía `onSubmit`, que recibe el `formData` ya tokenizado.
//
// `onSubmit` DEBE devolver una Promise: si resuelve, el Brick considera el
// pago OK; si rechaza, el Brick re-habilita el formulario para reintentar con
// otra tarjeta (justo lo que necesitamos para el rechazo inline del primer
// cobro).
//
// El Brick tokeniza la tarjeta internamente (no expone el PAN — PCI). El
// `formData.token` ES el `card_token_id` que usa el backend.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';
import { Loader2, AlertCircle } from 'lucide-react';
import { MP_PUBLIC_KEY } from '../config';
import type { CardFormData } from '../lib/mpSubscription';

// Init del SDK una sola vez por sesión (idempotente con guard propio).
let mpInitialized = false;
function ensureMpInit() {
  if (mpInitialized || !MP_PUBLIC_KEY) return;
  initMercadoPago(MP_PUBLIC_KEY, { locale: 'es-AR' });
  mpInitialized = true;
}

interface Props {
  /** Monto del primer cobro (ARS), requerido por el Brick. */
  amount: number;
  /** Email del payer (lo recolectamos antes, en el paso previo). */
  payerEmail?: string;
  /**
   * Recibe el formData tokenizado. Resolver = pago OK; rechazar = error
   * (el Brick re-habilita el form para reintentar).
   */
  onSubmit: (formData: CardFormData) => Promise<void>;
  /** Error del propio Brick (validación / init). */
  onBrickError?: (error: unknown) => void;
  /** Tamaño de fuente base del Brick (ej. '15px' desktop, '13px' mobile). */
  fontSize?: string;
}

export function MercadoPagoCardPayment({
  amount,
  payerEmail,
  onSubmit,
  onBrickError,
  fontSize = '15px',
}: Props) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    ensureMpInit();
  }, []);

  // `initialization` y `customization` DEBEN ser referencialmente estables: si
  // cambian, el SDK re-crea el Brick y tira "Ha ocurrido un error". Por eso el
  // email se captura UNA vez (el prefill async no debe re-inicializar) y los
  // objetos se memoizan.
  const initialEmailRef = useRef(payerEmail);
  const initialization = useMemo(
    () => ({
      amount,
      ...(initialEmailRef.current
        ? { payer: { email: initialEmailRef.current } }
        : {}),
    }),
    [amount],
  );
  const customization = useMemo(
    () =>
      ({
        // Suscripción: un solo pago recurrente, sin cuotas.
        paymentMethods: { maxInstallments: 1, minInstallments: 1 },
        visual: {
          style: {
            theme: 'default',
            // Alinea el Brick a la identidad visual (brand-primary).
            customVariables: {
              baseColor: '#1644EB',
              formBackgroundColor: '#FFFFFF',
              borderRadiusMedium: '6px',
              fontSizeMedium: fontSize,
            },
          },
        },
      }) as const,
    [fontSize],
  );

  // Callbacks estables: el Brick se re-crea si cambian de identidad. Usamos
  // refs a las versiones más nuevas y wrappers memoizados (que nunca cambian).
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onBrickErrorRef = useRef(onBrickError);
  onBrickErrorRef.current = onBrickError;
  const handleReady = useCallback(() => setReady(true), []);
  const handleSubmit = useCallback(
    (formData: unknown) =>
      onSubmitRef.current(formData as unknown as CardFormData),
    [],
  );
  const handleError = useCallback(
    (error: unknown) => onBrickErrorRef.current?.(error),
    [],
  );

  if (!MP_PUBLIC_KEY) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>Falta configurar la public key de MercadoPago.</span>
      </div>
    );
  }

  return (
    <div className="relative min-h-[120px]">
      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
        </div>
      )}
      <CardPayment
        initialization={initialization}
        customization={customization}
        onReady={handleReady}
        onSubmit={handleSubmit}
        onError={handleError}
      />
    </div>
  );
}

export default MercadoPagoCardPayment;
